import { CloseIcon, EditIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import {
  type SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useGetLoggedInUser } from '@revelio/auth';
import { BuildADashTrackingEvents, FeatureFlag } from '@revelio/core';
import { getGlobalAssistantReportCatalogEntry } from '@revelio/core/global-ai-assistant/reports';
import type {
  CompanyDiscoveryAiOutput,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import {
  useUnleashFlag,
  useUnleashFlags,
} from '../../shared/unleash-flags/use-unleash-flag';
import { trackAIAssistantPrompt } from '../CustomDash/components/AIAssistant/track-ai-assistant-prompt';
import { usePageManager } from '../CustomDash/components/PageManagerProvider/PageManagerProvider';
import {
  GLOBAL_AI_OVERLAY_BOTTOM,
  GLOBAL_AI_OVERLAY_HEIGHT,
  GLOBAL_AI_OVERLAY_RIGHT,
  GLOBAL_AI_OVERLAY_WIDTH,
  GLOBAL_AI_OVERLAY_Z_INDEX,
} from '../CustomDash/constants/panelWidths';
import SpeechInput, {
  type SpeechInputHandle,
} from '../CustomDash/filters/components/filterForms/talent-discovery/ai-search/SpeechInput';
import { useReportBuilderConfig } from '../reports/hooks/useReportBuilderConfig';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../reports/utils/report-builder-paths';
import { getPromotedTemplateForReportOfferClick } from '../reports/utils/report-template-promotions';
import type { NarrationCollector } from './ga-narration-collection-context';
import {
  getDeniedStandardNavigateOptionIds,
  getGlobalAssistantCustomDashAccess,
} from './global-ai-assistant-access';
import {
  type CustomDashAssistantEntityType,
  type GlobalAssistantContextPolicy,
  type GlobalAssistantEntityType,
  type GlobalAssistantReportOffer,
  type GlobalAssistantSuggestMatch,
  isCustomDashAssistantEntityType,
  postGlobalAssistantWarmUp,
  toCustomDashAssistantEntityType,
} from './global-ai-assistant-api';
import {
  COMMAND_TTL_MS,
  useGlobalAiCommandStore,
  waitForCommandComplete,
  waitForTaxonomyChange,
} from './global-ai-assistant-command-store';
import { useGlobalAIAssistant } from './global-ai-assistant-context';
import {
  applyConversationPolicy,
  resolveFollowOnContextPolicy,
  toConversationPayload,
} from './global-ai-assistant-context-policy';
import { buildBoundedConversationContext } from './global-ai-assistant-conversation';
import {
  buildGlobalAssistantCurrentPageContext,
  getDiscoveryApplyPrompt,
  isGlobalAssistantCurrentPageFilterable,
} from './global-ai-assistant-discovery-followup';
import styles from './global-ai-assistant-drawer.module.css';
import { isGlobalAssistantFilterApplyCapablePath } from './global-ai-assistant-filter-apply-capability';
import {
  confirmGlobalAiAssistantRedirect,
  getGlobalAiAssistantRoutePathname,
} from './global-ai-assistant-navigation-guard';
import { getGlobalAssistantPageMatchAction } from './global-ai-assistant-page-match-routing';
import { getGlobalAssistantReportAccess } from './global-ai-assistant-report-access';
import { launchReportFromOffer } from './global-ai-assistant-report-launch';
import type { GlobalAssistantResolvedRouteFilters } from './global-ai-assistant-route-filter-kind';
import { getGlobalAssistantRouteFilterKind } from './global-ai-assistant-route-filter-kind';
import { getRouteFilterResponse } from './global-ai-assistant-route-filter-prefetch';
import { resolveNavigateToOptionId } from './global-ai-assistant-standard-page-match';
import { buildPromptForSubmit } from './global-ai-assistant-submit-prompt';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import { requestGlobalAssistantWarmUpOnce } from './global-ai-assistant-warm-up';
import { formatReportPrefillNarration } from './global-ai-report-prefill-narration';
import {
  ASSISTANT_ICON_SIZE,
  AssistantAvatar,
  buildReportOfferLaunchKey,
  type ReportOfferCardFields,
  TranscriptMessages,
} from './global-ai-transcript-messages';
import {
  globalAssistantPathsMatch,
  inferEntityTypeFromNavigatePath,
} from './global-assistant-route-filter-path';
import { warmHeadlessDigestPath } from './headless-digest/headless-digest-service';
import type { ReasoningTrace } from './reasoning-trace';
import { runHeadlessBuildWithTrace } from './run-headless-build-with-trace';
import { beginSeededBuildNarration } from './seeded-build-narration';
import {
  buildReportPrefillDigest,
  type ReportPrefillDigest,
} from './turn-outcome-digest';
import { useGlobalAssistantTaxonomy } from './use-global-ai-assistant-taxonomy';
import { useGlobalAiOfferWiring } from './use-global-ai-offer-wiring';
import { useGlobalAiTurnContinuation } from './use-global-ai-turn-continuation';
import { useGlobalAiTurnRunner } from './use-global-ai-turn-runner';

// Append-only: the drawer keeps the existing prompt when voice input starts
// (unlike the AiSearchFilterForm, which clears it).
const noop = () => {
  // intentionally empty
};

const SAME_PAGE_FILTER_APPLY_MESSAGE = 'Applying these filters to this page.';

function findLatestClarification(
  transcript: GlobalAiTranscriptMessage[]
): Extract<GlobalAiTranscriptMessage, { kind: 'clarification' }> | undefined {
  for (let i = transcript.length - 1; i >= 0; i -= 1) {
    const m = transcript[i];
    if (m && m.role === 'assistant' && m.kind === 'clarification') {
      return m;
    }
    if (m && m.role === 'user') {
      return undefined;
    }
  }
  return undefined;
}

function countUserTurns(transcript: GlobalAiTranscriptMessage[]): number {
  return transcript.reduce((count, message) => {
    return message.role === 'user' ? count + 1 : count;
  }, 0);
}

function normalizeAndNavigateToPath(
  targetPath: string,
  navigate: ReturnType<typeof useNavigate>,
  skipGuard: boolean
): string {
  const normalizedPath = getGlobalAiAssistantRoutePathname(targetPath);
  if (normalizedPath === targetPath) {
    return normalizedPath;
  }
  navigate(
    normalizedPath,
    skipGuard
      ? { replace: true, state: { skipGuard: true } }
      : { replace: true }
  );
  return normalizedPath;
}

export function GlobalAIAssistantDrawer() {
  const {
    isDrawerOpen,
    closeDrawer,
    draftPromptRef,
    hasRequestedWarmUp,
    markWarmUpRequested,
    lastUserPrompt,
    setLastUserPrompt,
    setGenerationStatus,
    generationStatus,
    transcript,
    appendTranscript,
    updateReasoningTrace,
    updateAssistantMessageText,
    clearTranscript,
    sessionEntityScope,
    customDashAssistContextRef,
    conversationSummary,
    conversationTrackingIdRef,
    markNavConfirmResolved,
    markCreatePageConfirmResolved,
  } = useGlobalAIAssistant();

  const [draftPrompt, setDraftPromptLocal] = useState(
    () => draftPromptRef.current
  );
  const setDraftPrompt = useCallback(
    (value: SetStateAction<string>) => {
      setDraftPromptLocal((previous) => {
        const next = typeof value === 'function' ? value(previous) : value;
        draftPromptRef.current = next;
        return next;
      });
    },
    [draftPromptRef]
  );

  // Command store: drawer emits, bridge (inside CustomDash) handles.
  // Subscribe to the two slices separately so changes to in-flight state
  // don't re-render the drawer when emit identity hasn't changed.
  const emit = useGlobalAiCommandStore((state) => state.emit);
  // Per-entity taxonomy resolver — SAVED PREFS ONLY, single source of truth.
  //
  // Request taxonomy must equal what the destination page's pref-sync will
  // apply (the scoped stored preference): that invariant is what keeps
  // text = artifact link = landed page consistent. The bridge-published
  // `activeTaxonomyByEntity` used to override prefs here, which made the
  // request taxonomy depend on WHICH page happened to be mounted when the
  // user asked — same account, same question, different answers. Known
  // trade-off (product decision): session-only unsaved overrides from the
  // Preferences modal are no longer reflected in assistant requests.
  //
  // The bridge PUBLISH itself stays: `waitForTaxonomyChange` (the
  // adaptive-company refinement pass in `navigateToMatch`) still consumes
  // the store's `activeTaxonomyByEntity` to detect post-apply adaptive
  // re-resolution. TODO(backend-adaptive): delete the publish path with
  // that refinement block once the backend accepts "adaptive".
  const taxonomyByEntity = useGlobalAssistantTaxonomy();
  const lastAppliedFilterContextByPath = useGlobalAiCommandStore(
    (state) => state.lastAppliedFilterContextByPath
  );
  const isCommandInFlight = useGlobalAiCommandStore(
    (state) => state.isCommandInFlight
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { createPageDraft } = usePageManager();
  const { loggedInUser } = useGetLoggedInUser();
  const customDashNavEnabled = useUnleashFlag(
    FeatureFlag.CustomDashViewEditToggle
  );
  const userTabs = useMemo(() => loggedInUser.tabs ?? [], [loggedInUser.tabs]);
  const customDashAccess = useMemo(
    () => getGlobalAssistantCustomDashAccess(customDashNavEnabled),
    [customDashNavEnabled]
  );
  const customReportsIsEnabled = useUnleashFlag(FeatureFlag.CustomReports);
  const allFlags = useUnleashFlags();
  const { config: reportBuilderConfig } = useReportBuilderConfig();

  // Single source of truth for "a turn is running". Set true immediately
  // before `runTurn` starts and cleared in a `finally` after it settles, so
  // the drawer input is disabled and overlapping `runTurn` calls are blocked
  // for the full duration of a loop turn.
  const [isTurnRunning, setIsTurnRunning] = useState(false);
  const [reportOfferLaunchingKey, setReportOfferLaunchingKey] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const isReportOfferLaunching = reportOfferLaunchingKey !== null;
  const toast = useToast();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const speechInputRef = useRef<SpeechInputHandle>(null);
  const drawerPanelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Mirror of generationStatus so async callbacks (e.g. the safety
  // timeout in navigateToMatch) can read the latest value without
  // capturing a stale closure.
  const generationStatusRef = useRef<string | null>(generationStatus);
  generationStatusRef.current = generationStatus;
  // Latest transcript, read by async callbacks (e.g. report-prefill
  // narration) that resolve after the click handler's closure was created
  // and need the freshest assistant text message to target.
  const latestTranscriptRef = useRef(transcript);
  latestTranscriptRef.current = transcript;
  // Live pathname for callbacks that fire long after their closure was
  // created (the seeded-build narration collector completes up to 30s after
  // the click) — a captured `location.pathname` would be stale by then.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  // Collector for a resolve-first build's landing narration (seeded-build-
  // narration.ts). Drawer-owned dispose-previous slot, mirroring the bridge's
  // narrationCollectorRef: a re-clicked build chip must supersede the
  // previous collection, never race it.
  const seededBuildCollectorRef = useRef<NarrationCollector | null>(null);
  const [transcriptScrollRoot, setTranscriptScrollRoot] =
    useState<HTMLDivElement | null>(null);
  const handleTranscriptScrollRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      setTranscriptScrollRoot((previousNode) => {
        return previousNode === node ? previousNode : node;
      });
    },
    []
  );
  // FE-2172: when a new prompt is submitted, pin it to the top of the transcript
  // with empty space below (so the incoming answer streams in underneath without
  // shoving the question off-screen). `lastUserMessageRef` anchors the scroll;
  // `bottomSpacerHeight` reserves the room needed for the question to reach the
  // top, shrinking as the answer fills the viewport.
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const [bottomSpacerHeight, setBottomSpacerHeight] = useState(0);
  const lastUserMessageId = useMemo(() => {
    for (let i = transcript.length - 1; i >= 0; i -= 1) {
      const message = transcript[i];
      if (message?.role === 'user') {
        return message.id;
      }
    }
    return undefined;
  }, [transcript]);
  const userTurnCount = countUserTurns(transcript);
  const prevUserTurnCountRef = useRef(userTurnCount);
  // Tracks the turn count the spacer was last measured against, so the measure
  // effect can tell a brand-new question (needs reserving) from ongoing token
  // streaming within the same turn (only needs re-measuring while the spacer is
  // still shrinking).
  const measuredTurnCountRef = useRef(userTurnCount);

  const pathEntity = useMemo(
    () => inferEntityTypeFromNavigatePath(location.pathname, 'company'),
    [location.pathname]
  );

  const confirmAssistantRedirect = (targetPath?: string) => {
    return confirmGlobalAiAssistantRedirect({
      customDashContext: customDashAssistContextRef.current,
      currentPathname: location.pathname,
      targetPath,
    });
  };

  const onCloseAndStopRecording = useCallback(() => {
    speechInputRef.current?.stopRecording();
    closeDrawer();
  }, [closeDrawer]);

  useEffect(() => {
    return () => {
      speechInputRef.current?.stopRecording();
    };
  }, []);

  useEffect(() => {
    requestGlobalAssistantWarmUpOnce({
      isDrawerOpen,
      hasRequested: hasRequestedWarmUp,
      markRequested: markWarmUpRequested,
      onWarmUpError: (error) => {
        console.debug('Global assistant warm-up failed', error);
      },
      onWarmUpResult: (response) => {
        console.debug('Global assistant warm-up response', response);
      },
      requestWarmUp: postGlobalAssistantWarmUp,
    });
    if (isDrawerOpen) {
      // FE-side twin of the backend warm-up above: prime the headless digest
      // path's data caches (selection lists, taxonomy trees, prefs) so the
      // session's first direct answer skips the ~4-5s cold-mount tax.
      warmHeadlessDigestPath();
    }
  }, [isDrawerOpen, hasRequestedWarmUp, markWarmUpRequested]);

  // apply_filters is optimistic (DP4): the turn ends when the command is
  // ENQUEUED, so an apply the chat already promised can still die two ways —
  // the command ages out unconsumed (TTL) or a bridge consumes it and the
  // apply throws. Surface either as an in-chat message; the ref dedupes so
  // each failure event surfaces exactly once.
  const lastFilterApplyFailure = useGlobalAiCommandStore(
    (state) => state.lastFilterApplyFailure
  );
  const surfacedFailureAtRef = useRef(0);
  useEffect(() => {
    if (!lastFilterApplyFailure) {
      return;
    }
    if (lastFilterApplyFailure.at <= surfacedFailureAtRef.current) {
      return;
    }
    surfacedFailureAtRef.current = lastFilterApplyFailure.at;
    const where = lastFilterApplyFailure.paths.join(', ');
    const why =
      lastFilterApplyFailure.reason === 'error'
        ? 'the page hit an error while applying them'
        : "the page didn't confirm in time";
    appendTranscript({
      id: crypto.randomUUID(),
      role: 'assistant',
      kind: 'text',
      text: `I couldn't finish applying those filters to ${where} — ${why}. Please try again.`,
    });
  }, [lastFilterApplyFailure, appendTranscript]);

  useEffect(() => {
    if (!isDrawerOpen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseAndStopRecording();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDrawerOpen, onCloseAndStopRecording]);

  // The drawer stays mounted while closed (hidden via opacity), so the transcript
  // keeps whatever scroll position it had. On (re)open, jump to the latest
  // messages. Deferred a frame so the open layout has settled and scrollHeight is
  // final.
  useEffect(() => {
    if (!isDrawerOpen || !transcriptScrollRoot) {
      return;
    }
    const raf = requestAnimationFrame(() => {
      transcriptScrollRoot.scrollTop = transcriptScrollRoot.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [isDrawerOpen, transcriptScrollRoot]);

  // FE-2172: reserve just enough space below the latest question so it can sit
  // at the top of the viewport. Measured before paint so the pin-to-top scroll
  // below lands correctly. Subtracting the current spacer from `scrollHeight`
  // recovers the real content height, so this converges without jitter.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on any content/size change
  useLayoutEffect(() => {
    const isNewQuestion = userTurnCount !== measuredTurnCountRef.current;
    // Confine the (forced-synchronous) layout read to the brief reservation
    // window: a new question just arrived, or the spacer is still shrinking
    // toward zero. Once it has collapsed and no new question has come in, skip
    // the read entirely — otherwise a long streaming answer would force a reflow
    // on every token even though the spacer is already 0 (Greptile P2).
    if (!isNewQuestion && bottomSpacerHeight === 0) {
      return;
    }
    const root = scrollRef.current;
    const userNode = lastUserMessageRef.current;
    if (!root || !userNode) {
      // Nodes not mounted yet — leave `measuredTurnCountRef` untouched so a new
      // question is retried on the next commit rather than silently skipped.
      if (bottomSpacerHeight !== 0) {
        setBottomSpacerHeight(0);
      }
      return;
    }
    measuredTurnCountRef.current = userTurnCount;
    const questionTop =
      userNode.getBoundingClientRect().top -
      root.getBoundingClientRect().top +
      root.scrollTop;
    const realContentHeight = root.scrollHeight - bottomSpacerHeight;
    const contentBelowQuestion = realContentHeight - questionTop;
    const desired = Math.max(
      0,
      Math.round(root.clientHeight - contentBelowQuestion)
    );
    if (Math.abs(desired - bottomSpacerHeight) > 1) {
      setBottomSpacerHeight(desired);
    }
  }, [
    bottomSpacerHeight,
    lastUserMessageId,
    transcript,
    isTurnRunning,
    generationStatus,
    isCommandInFlight,
    reportOfferLaunchingKey,
    transcriptScrollRoot,
  ]);

  // FE-2172: on a NEW question (the user-turn count grew), scroll it to the top.
  // For the rest of the turn we leave the scroll position alone, so the answer
  // streams in below the pinned question rather than yanking the view down.
  // rAF defers the scroll until after the spacer above has been laid out.
  useEffect(() => {
    const grew = userTurnCount > prevUserTurnCountRef.current;
    prevUserTurnCountRef.current = userTurnCount;
    if (!grew) {
      return;
    }
    const id = requestAnimationFrame(() => {
      const root = scrollRef.current;
      const userNode = lastUserMessageRef.current;
      if (!root || !userNode) {
        return;
      }
      const top =
        userNode.getBoundingClientRect().top -
        root.getBoundingClientRect().top +
        root.scrollTop;
      root.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [userTurnCount]);

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDraftPrompt(value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (textareaRef.current && isDrawerOpen) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [draftPrompt, isDrawerOpen]);

  const handleNewSession = () => {
    clearTranscript();
    conversationTrackingIdRef.current = crypto.randomUUID();
    setError(null);
    setDraftPrompt('');
    setLastUserPrompt(null);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  // Resolve the prompt's filters and, ON SUCCESS, emit the resolved
  // `applyRouteFilters` command (+ fire the adaptive refinement pass).
  // Returns whether resolution succeeded so RESOLVE-FIRST navigation
  // (meeting decision 2026-07-08) can await it and only navigate on success —
  // no more landing on a default page that visibly re-filters seconds later.
  const resolveThenEmitRouteFilters = useCallback(
    async (args: {
      path: string;
      promptForFilters: string;
      entityType: GlobalAssistantEntityType;
      requestId?: string;
    }): Promise<boolean> => {
      const { path, promptForFilters, entityType } = args;
      const requestId = args.requestId ?? crypto.randomUUID();
      const filterKind = getGlobalAssistantRouteFilterKind(path);
      mixpanel.track(
        BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED,
        {
          entity_type: entityType,
          global_assistant_route_filter_request_id: requestId,
        }
      );
      const RESOLVING_STATUS = 'Resolving filters…';
      setGenerationStatus(RESOLVING_STATUS);
      // Safety net: if no bridge ever claims this command (e.g. user
      // navigates elsewhere, or the target page has an incompatible
      // entity type), the queue entry expires at COMMAND_TTL_MS and the
      // "Resolving filters…" status would otherwise stay forever. Clear
      // it once the queue TTL has passed, but only if the status we set
      // is still the one on screen — a bridge may have legitimately
      // moved it to "Applying filters…" or an error, and we don't want
      // to wipe those.
      const clearIfStillResolving = setTimeout(() => {
        if (generationStatusRef.current === RESOLVING_STATUS) {
          setGenerationStatus(null);
        }
      }, COMMAND_TTL_MS);
      // Use whatever taxonomy is available right now — the store's
      // published value if any, or saved user prefs. For company-entity
      // + adaptive this may be wrong (adaptive resolution depends on the
      // target page's company filter, which the AI is about to apply).
      // The refinement pass below catches that case.
      const initialTaxonomy = taxonomyByEntity(entityType);
      try {
        const resolved = await getRouteFilterResponse(
          promptForFilters,
          initialTaxonomy,
          {
            filterKind,
            screenerEntityType: toCustomDashAssistantEntityType(entityType),
          }
        );
        emit({
          kind: 'applyRouteFilters',
          requestId,
          ...resolved,
          targetPath: path,
          entityType,
          prompt: promptForFilters,
        });
        // Screener uses a different endpoint; do not run TD-only refinement.
        if (filterKind !== 'tdV2') return true;
        // TODO(backend-adaptive): DELETE this entire refinement block
        // once the AI filter search endpoint accepts "adaptive" as a
        // taxonomy value.
        //
        // Why this exists today:
        // On company-entity pages with an adaptive role/skill taxonomy
        // preference, the "correct" taxonomy is a function of which
        // companies are in the filter map. But the AI response ITSELF
        // is what dispatches those companies — chicken-and-egg. So the
        // initial request above uses an approximate taxonomy (the
        // bridge's first-pass publish, which pre-dates the AI's
        // company filter), the bridge applies it, `useAdaptiveRoleTaxonomy`
        // / `useAdaptiveSkillTaxonomy` re-resolve with the new company
        // in scope, the bridge republishes with the corrected value,
        // and THIS block fires a second request+apply to refine.
        //
        // Why the future work eliminates it:
        // If the backend accepts `roles_tax: 'adaptive'` (and
        // `skills_tax: 'adaptive'` or equivalent), the drawer can send
        // "adaptive" directly, the backend computes the company-based
        // RICS resolution server-side using the same prompt-derived
        // company intent, and the response is correct on the first try.
        //
        // Other deletable pieces tagged with TODO(backend-adaptive):
        //   - `waitForCommandComplete` + `waitForTaxonomyChange` helpers
        //     in `global-ai-assistant-command-store.ts` (only this block
        //     uses them)
        //   - Bridge's `isRoleTaxonomyResolved`/`isSkillTaxonomyResolved`
        //     gate in `global-ai-assistant-bridge.tsx`
        //   - Store's `activeTaxonomyByEntity` + `publishActiveTaxonomy`
        //     (and the whole bridge publish-effect that feeds them) —
        //     this refinement block is now their ONLY consumer; the
        //     drawer's request payload reads saved prefs exclusively
        if (entityType !== 'company' && entityType !== 'talentDiscovery') {
          return true;
        }
        void (async () => {
          const COMMAND_COMPLETE_WAIT_MS = 10_000;
          const TAXONOMY_CHANGE_WAIT_MS = 3_000;
          const initialCompleted = await waitForCommandComplete(
            requestId,
            COMMAND_COMPLETE_WAIT_MS
          );
          if (!initialCompleted) return;
          const refinedTaxonomy = await waitForTaxonomyChange(
            entityType,
            initialTaxonomy,
            TAXONOMY_CHANGE_WAIT_MS
          );
          if (refinedTaxonomy === null) return;
          try {
            // Force a fresh fetch: the cache key (by preference
            // identity) would otherwise hit the initial entry and
            // return the wrong-taxonomy response, defeating the
            // refinement's purpose.
            //
            // Keys stay from `initialTaxonomy` — the user's preference
            // identity doesn't change between the initial and refined
            // passes, only the resolved numeric does.
            const refinedResolved = await getRouteFilterResponse(
              promptForFilters,
              {
                rolesTax: refinedTaxonomy.rolesTax,
                skillsTax: refinedTaxonomy.skillsTax,
                rolesKey: initialTaxonomy.rolesKey,
                skillsKey: initialTaxonomy.skillsKey,
              },
              { bypassCache: true, filterKind: 'tdV2' }
            );
            emit({
              kind: 'applyRouteFilters',
              requestId: crypto.randomUUID(),
              ...refinedResolved,
              targetPath: path,
              entityType,
              prompt: promptForFilters,
            });
          } catch {
            // Refinement is best-effort; the initial apply has already
            // landed so the user has SOME filters applied.
          }
        })();
        return true;
      } catch (err) {
        clearTimeout(clearIfStillResolving);
        const message = err instanceof Error ? err.message : 'Failed';
        mixpanel.track(
          BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_FAILED,
          {
            entity_type: entityType,
            global_assistant_route_filter_error: message,
          }
        );
        setGenerationStatus(`Couldn't resolve filters: ${message}`);
        return false;
      }
    },
    [emit, setGenerationStatus, taxonomyByEntity]
  );

  // Fire-and-forget wrapper for the CURRENT-page apply chip (no navigation
  // involved, so nothing awaits the resolution).
  const queueRouteFilterApply = useCallback(
    (args: {
      path: string;
      promptForFilters: string;
      entityType: GlobalAssistantEntityType;
      requestId?: string;
    }) => {
      void resolveThenEmitRouteFilters(args);
    },
    [resolveThenEmitRouteFilters]
  );

  const narrateReportPrefill = useCallback(
    (params: { digest: ReportPrefillDigest }) => {
      // The prefill digest is fully structured, so the confirmation line is
      // built deterministically — no LLM, no network call. APPENDED as a new
      // message, never written into an existing bubble: the latest text is
      // often a STAMPED offer narration (inline [Report](offer:report)
      // trigger), and rewriting it deletes the marker — which un-suppresses
      // the report cards and destroys the clickable explanation.
      appendTranscript({
        id: crypto.randomUUID(),
        role: 'assistant',
        kind: 'text',
        text: formatReportPrefillNarration(params.digest),
      });
    },
    [appendTranscript]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: confirmAssistantRedirect reads customDashAssistContextRef at click time
  const handleReportOfferClick = useCallback(
    async (
      offer: ReportOfferCardFields,
      prompt: string | undefined,
      messageId: string
    ) => {
      const promotedTemplate = getPromotedTemplateForReportOfferClick(
        offer.reportType,
        allFlags,
        customReportsIsEnabled
      );
      const access = getGlobalAssistantReportAccess(
        offer.reportType,
        offer.reportName,
        reportBuilderConfig,
        promotedTemplate !== null
      );
      if (!access.allowed) {
        appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'error',
          text: access.message,
        });
        return;
      }

      setReportOfferLaunchingKey(
        buildReportOfferLaunchKey(messageId, offer.reportType)
      );
      try {
        mixpanel.track(
          BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_CLICKED,
          {
            global_assistant_report_type: offer.reportType,
          }
        );

        const entityPath = getReportBuilderPath(ReportBuilderStep.ENTITY);
        const guard = confirmAssistantRedirect(entityPath);
        if (!guard.shouldContinue) {
          appendTranscript({
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'text',
            text: 'Navigation canceled. I kept you on this page so your unsaved changes remain.',
          });
          return;
        }

        const promptForParams = prompt?.trim() || lastUserPrompt?.trim() || '';
        const roleTaxonomy = taxonomyByEntity('role');

        await launchReportFromOffer({
          offer: {
            reportType: offer.reportType,
            reportName: offer.reportName,
            confidence: offer.confidence,
            sampleLink: offer.sampleLink,
            description: offer.description ?? '',
          } satisfies GlobalAssistantReportOffer,
          promptForParams,
          roleTaxonomy,
          promotedTemplate,
          reportEntityMap: reportBuilderConfig,
          pathname: location.pathname,
          navigate,
          skipGuard: guard.skipGuard,
          onPrefillError: (text) => {
            appendTranscript({
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'error',
              text,
            });
          },
          onPrefillWarning: (text) => {
            toast({
              title: 'Some report filters need manual selection',
              description: text,
              status: 'warning',
              duration: 7000,
              isClosable: true,
              position: 'top-right',
            });
          },
          onPrefillSuggestions: (suggestions) => {
            narrateReportPrefill({
              digest: buildReportPrefillDigest({
                reportName: offer.reportName,
                suggestions,
              }),
            });
          },
        });
        if (guard.skipGuard) {
          guard.discardChanges?.();
        }
      } finally {
        setReportOfferLaunchingKey(null);
      }
    },
    [
      allFlags,
      appendTranscript,
      customReportsIsEnabled,
      lastUserPrompt,
      location.pathname,
      navigate,
      narrateReportPrefill,
      reportBuilderConfig,
      taxonomyByEntity,
      toast,
    ]
  );

  const handleSubmit = async () => {
    const rawPrompt = draftPrompt.trim();
    if (!rawPrompt || isReportOfferLaunching || isTurnRunning) {
      return;
    }
    const trimmed = buildPromptForSubmit({
      rawPrompt,
      lastUserPrompt,
      transcript,
    });
    setError(null);
    setDraftPrompt('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    // Clarification replies are classified by the router LLM via
    // conversation history + pendingClarification + pendingClarificationResolution.
    const lastClarification = findLatestClarification(transcript);
    const promptMode = lastClarification ? 'clarification_answer' : 'generate';
    trackAIAssistantPrompt({
      assistantSurface: 'global',
      conversationId: conversationTrackingIdRef.current,
      turnIndex: countUserTurns(transcript) + 1,
      promptText: trimmed,
      promptMode,
      entityType: pathEntity,
    });
    setLastUserPrompt(trimmed);
    appendTranscript({
      id: crypto.randomUUID(),
      role: 'user',
      text: trimmed,
    });
    setIsTurnRunning(true);
    try {
      await runTurn(trimmed);
    } finally {
      setIsTurnRunning(false);
    }
  };

  // Programmatically start an assistant turn seeded with `prompt`, reusing the
  // exact same machinery as `handleSubmit` (append a user message, guard
  // `isTurnRunning`, run the turn). Used by the nav-confirm chip to RESUME a
  // chained task after the user confirms navigation. Bails if a turn is
  // already running so a click can't stack overlapping turns.
  const startAssistantTurn = async (
    prompt: string,
    options?: {
      /**
       * Silent seed: run a REAL turn (the model receives `prompt` as the turn's
       * `userPrompt`) but DON'T render the seed as a chat bubble. Used by the
       * confirm-navigate-resume flows, whose seed is a bracketed `[System: …]`
       * instruction the user must never see. `runTurn` passes `prompt` to the
       * loop as `userPrompt` SEPARATELY from `priorMessages` (built from the
       * transcript), so the model still gets the seed without the transcript
       * append. We also skip `setLastUserPrompt` so the bracketed instruction
       * never becomes the prompt a later chip (e.g. "build the dashboard")
       * resolves from. The turn is still tracked + guarded as a normal turn.
       */
      silent?: boolean;
      /**
       * Skip the post-loop report ride-along for this turn. Used by the build
       * resume: a matching report was already offered alongside the Build
       * button, so re-offering it after the build is noise.
       */
      suppressReportOffer?: boolean;
    }
  ): Promise<void> => {
    const trimmed = prompt.trim();
    if (!trimmed || isTurnRunning) {
      return;
    }
    if (!options?.silent) {
      // Non-silent resume turns are tracked + rendered like a normal prompt.
      // Silent turns are system-seeded continuations (e.g. the build resume's
      // "[System: …]" instruction): the user's real prompt was already tracked
      // when they first submitted it, so we neither re-track it (avoiding a
      // double-count) nor log the 250+ char seed as a user utterance — and it
      // never renders as a chat bubble. `pathEntity` already reflects the
      // DESTINATION page (this runs after the navigation effect).
      trackAIAssistantPrompt({
        assistantSurface: 'global',
        conversationId: conversationTrackingIdRef.current,
        turnIndex: countUserTurns(transcript) + 1,
        promptText: trimmed,
        promptMode: 'generate',
        entityType: pathEntity,
      });
      setLastUserPrompt(trimmed);
      appendTranscript({
        id: crypto.randomUUID(),
        role: 'user',
        text: trimmed,
      });
    }
    setIsTurnRunning(true);
    try {
      await runTurn(trimmed, {
        suppressReportOffer: options?.suppressReportOffer,
      });
    } finally {
      setIsTurnRunning(false);
    }
  };
  // Latest startAssistantTurn, for callbacks that fire long after their
  // closure was created (the seeded-build landing narration completes up to
  // 30s after the click): the fresh closure carries the CURRENT
  // isTurnRunning guard, so a stale capture can't start a turn mid-turn.
  const startAssistantTurnRef = useRef(startAssistantTurn);
  startAssistantTurnRef.current = startAssistantTurn;

  const continuation = useGlobalAiTurnContinuation({
    isTurnRunning,
    startAssistantTurn,
    transcriptScrollRoot,
  });

  // Returns whether navigation actually proceeded. The redirect guard can
  // cancel (unsaved changes), in which case we report `navigated: false` so
  // loop-initiated callers don't narrate a navigation that never happened
  // (FE-2117). Existing chip callers ignore the return value.
  const navigateToMatch = async (
    match: GlobalAssistantSuggestMatch,
    options?: {
      standard?: boolean;
      prompt?: string;
      skipFilterApply?: boolean;
      /**
       * ALREADY-resolved filter artifact to apply on the destination — the
       * exact `search_filters` response a single-value answer used. When
       * present, NO prompt-based re-resolution runs on this navigation: the
       * artifact is emitted directly as a resolved `applyRouteFilters`
       * command (replace semantics — a fresh landing) that the destination
       * bridge claims after the router lands. This is what keeps an
       * `offer:page` click deterministic: the page shows the SAME filter set
       * the answer was computed from, never an independent AI resolution of
       * the prompt (which can land on a different filter set entirely).
       */
      resolvedFilters?: TalentDiscoveryAiFilterSearchResponseV2;
      /**
       * Entity type the resolved artifact was resolved under. Falls back to
       * the path-inferred entity type when omitted. Only read alongside
       * `resolvedFilters`.
       */
      resolvedEntityType?: GlobalAssistantEntityType;
    }
  ): Promise<{ navigated: boolean }> => {
    const path = match.navigatePath;
    // Prefer the prompt that produced THIS match (carried on the match via
    // `match.prompt`) so clicking a chip from an earlier prompt — or a
    // hydrated session — resolves filters for the correct prompt. Falls back
    // to `lastUserPrompt` for older persisted messages that don't carry it.
    // When `skipFilterApply` is true (loop-initiated pure navigation) the
    // prompt is intentionally left undefined so no filter application occurs.
    // Likewise with `resolvedFilters`: the artifact IS the filter set, so the
    // prompt must never feed a re-resolution (discovery search included).
    const promptForFilters =
      options?.skipFilterApply || options?.resolvedFilters
        ? undefined
        : getDiscoveryApplyPrompt(
            match,
            options?.prompt?.trim() || lastUserPrompt?.trim() || undefined
          );
    const action = getGlobalAssistantPageMatchAction(match, promptForFilters);
    const entityType = inferEntityTypeFromNavigatePath(
      path,
      sessionEntityScope ?? pathEntity
    );
    const requestId = crypto.randomUUID();
    const isAlreadyOnTargetPage = globalAssistantPathsMatch(
      location.pathname,
      path
    );
    const guard = confirmAssistantRedirect(path);
    if (!guard.shouldContinue) {
      appendTranscript({
        id: crypto.randomUUID(),
        role: 'assistant',
        kind: 'text',
        text: 'Navigation canceled. I kept you on this page so your unsaved changes remain.',
      });
      return { navigated: false };
    }

    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: options?.standard ? 'standard_template_page' : 'existing_page',
      navigate_path: path,
    });

    if (options?.resolvedFilters) {
      // Deterministic apply: emit the ALREADY-resolved artifact as a resolved
      // `applyRouteFilters` command, then navigate — the same
      // emit-before-navigate sequencing `queueRouteFilterApply` uses (the
      // command waits in the store until the destination bridge mounts and
      // claims it by targetPath + entityType; see the command-store docblock).
      // Same mechanics as the turn runner's `applyFilters` handle: no
      // `getRouteFilterResponse`, no discovery re-search — re-resolution must
      // never happen on this path. Replace semantics (no `merge`): the click
      // is a fresh landing on the answer's exact filter scope.
      const STATUS_MESSAGE = 'Applying filters…';
      setGenerationStatus(STATUS_MESSAGE);
      setTimeout(() => {
        if (generationStatusRef.current === STATUS_MESSAGE) {
          setGenerationStatus(null);
        }
      }, COMMAND_TTL_MS);
      emitResolvedRouteFilters({
        requestId,
        path,
        response: options.resolvedFilters,
        // Metadata only (tracking + bridge context); NEVER used to resolve.
        prompt: options?.prompt?.trim() || lastUserPrompt?.trim() || '',
        entityType: options.resolvedEntityType ?? entityType,
        // The artifact's role ids are taxonomy-relative — the destination
        // page must adopt the taxonomy they were resolved under, or it
        // queries them under the user's preference taxonomy and the backend
        // rejects the request (`role_v3_id = []`). This is a user-initiated
        // "show me this answer's filters" click, so pairing IS the correct
        // page behavior here (unlike blanket AI applies, which stay
        // unpaired by design).
        pairTaxonomy: true,
      });
      if (!isAlreadyOnTargetPage) {
        navigate(
          path,
          guard.skipGuard ? { state: { skipGuard: true } } : undefined
        );
      }
      guard.discardChanges?.();
      return { navigated: true };
    }

    if (action.kind === 'apply_discovery_filters_after_navigate') {
      mixpanel.track(
        BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_ROUTE_FILTER_QUEUED,
        {
          ...(action.discoveryKind === 'talent'
            ? {}
            : { entity_type: action.discoveryKind }),
          global_assistant_discovery_kind: action.discoveryKind,
          global_assistant_route_filter_request_id: requestId,
        }
      );
      const STATUS_MESSAGE = isAlreadyOnTargetPage
        ? 'Applying filters…'
        : 'Opening Discovery…';
      setGenerationStatus(STATUS_MESSAGE);
      setTimeout(() => {
        if (generationStatusRef.current === STATUS_MESSAGE) {
          setGenerationStatus(null);
        }
      }, COMMAND_TTL_MS);
      // RESOLVE-FIRST (meeting decision 2026-07-08): the discovery search used
      // to run on the DESTINATION after landing (runDiscoveryAiSearchAfterNavigate,
      // deleted with this change) — the user watched an unfiltered surface
      // resolve. Resolve drawer-side instead; the shared resolver already has
      // bridge parity for TD (dateGranularity 'month', adaptive taxonomy
      // flags) and the screener (screenerAiSearchRequest). Then emit the
      // resolved apply and navigate — the destination mounts with its command
      // waiting. Failure: stay put and say so.
      const discoveryResolvedOk = await resolveThenEmitRouteFilters({
        path,
        promptForFilters: promptForFilters ?? '',
        entityType,
        requestId,
      });
      if (!discoveryResolvedOk) {
        appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'error',
          text: `Couldn't resolve that search for ${match.name}, so I stayed on this page — you can retry, or open ${match.name} from the navigation and search there.`,
        });
        return { navigated: false };
      }
      if (!isAlreadyOnTargetPage) {
        navigate(
          path,
          guard.skipGuard ? { state: { skipGuard: true } } : undefined
        );
      }
      guard.discardChanges?.();
      return { navigated: true };
    }

    if (action.kind === 'resolve_route_filters' && promptForFilters) {
      if (isAlreadyOnTargetPage) {
        appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'text',
          text: SAME_PAGE_FILTER_APPLY_MESSAGE,
        });
      }
      // RESOLVE-FIRST (meeting decision 2026-07-08): await resolution BEFORE
      // navigating, so the destination mounts with its resolved apply command
      // already waiting in the store — one landing, no default-page flicker.
      // The drawer's 'Resolving filters…' status covers the wait. On failure:
      // stay put and say so; landing unfiltered used to masquerade as success.
      const resolvedOk = await resolveThenEmitRouteFilters({
        path,
        promptForFilters,
        entityType,
        requestId,
      });
      // Check failure BEFORE the same-page success return: when already on the
      // target page and resolution FAILS, an early `navigated: true` here would
      // shadow this error path — the user would see the optimistic "applying
      // these filters" line, no apply command would be emitted, and the caller
      // would get success. Failure must surface (and report `navigated: false`)
      // whether or not we needed to navigate.
      if (!resolvedOk) {
        appendTranscript({
          id: crypto.randomUUID(),
          role: 'assistant',
          kind: 'error',
          text: `Couldn't resolve the filters for ${match.name}, so I stayed on this page — you can retry, or open ${match.name} unfiltered from the navigation.`,
        });
        return { navigated: false };
      }
      if (isAlreadyOnTargetPage) {
        guard.discardChanges?.();
        return { navigated: true };
      }
    }

    navigate(
      path,
      guard.skipGuard ? { state: { skipGuard: true } } : undefined
    );
    guard.discardChanges?.();
    return { navigated: true };
  };

  const startCustomPageCreate = (
    entityType: CustomDashAssistantEntityType,
    options?: {
      prompt?: string;
      contextPolicy?: GlobalAssistantContextPolicy;
      /**
       * Draft-only mode: create the empty page draft and navigate to it, but do
       * NOT emit the `startCustomPageStream` build command. Used by the
       * confirm-navigate-resume flow (the chip click navigates to an empty
       * editable draft, then RESUMES the loop turn so the model builds the page
       * itself via `update_current_page` — native reasoning trace + grounded
       * narration). The normal build path leaves this unset.
       */
      skipBuild?: boolean;
    }
  ): { ok: boolean; targetPath?: string } => {
    if (!customDashAccess.allowed) {
      setError(customDashAccess.message);
      return { ok: false };
    }
    // Prefer the prompt attached to the message that triggered the click
    // (so the "Create custom page" button still works after session
    // hydration, where `lastUserPrompt` is not restored from localStorage).
    const text = options?.prompt?.trim() || lastUserPrompt?.trim() || undefined;
    if (!text) {
      setError('Run a prompt first so we know what to build.');
      return { ok: false };
    }
    const guard = confirmAssistantRedirect();
    if (!guard.shouldContinue) {
      return { ok: false };
    }
    const targetPath = createPageDraft(entityType, guard.skipGuard);
    if (!targetPath) {
      return { ok: false };
    }
    const targetPathname = normalizeAndNavigateToPath(
      targetPath,
      navigate,
      guard.skipGuard
    );
    const isSamePath = globalAssistantPathsMatch(
      location.pathname,
      targetPathname
    );
    if (!isSamePath) {
      guard.discardChanges?.();
    }
    // Draft-only: created the empty draft + navigated. Skip the build emit and
    // hand the target path back so the caller can arm a pending resume keyed on
    // it (the resumed turn builds the page on this draft).
    if (options?.skipBuild) {
      return { ok: true, targetPath: targetPathname };
    }
    const contextPolicy = resolveFollowOnContextPolicy(
      options?.contextPolicy,
      text
    );
    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: 'custom_page',
      global_assistant_context_policy: contextPolicy,
    });
    const fullConversationContext = buildBoundedConversationContext({
      transcript,
      summary: conversationSummary,
    });
    const conversationPayload = toConversationPayload(
      applyConversationPolicy({
        policy: contextPolicy,
        fullConversation: fullConversationContext,
      })
    );
    emit({
      kind: 'startCustomPageStream',
      requestId: crypto.randomUUID(),
      prompt: text,
      entityType,
      mode: 'create',
      targetPath: targetPathname,
      ...(conversationPayload ? { conversation: conversationPayload } : {}),
    });
    setGenerationStatus('Opening custom page…');
    return { ok: true, targetPath: targetPathname };
  };

  // LAZY click-time generation for build chips WITHOUT a pre-generated
  // layout (offer_page_or_custom's secondary chip — pre-generating for every
  // partial-fit offer would tax causal asks whose page link is the usual
  // click). Runs the page-gen stream headlessly while the user is STILL on
  // the current page, streaming its reasoning into a self-contained trace
  // (live thinking → "Worked for Xs") exactly like a turn's create step — the
  // static "Generating the dashboard…" status hid the model's work (live
  // complaint). The wiring then stashes the layout and navigates to a COMPLETE
  // draft (resolve-first, meeting 2026-07-08). Null → the wiring falls back to
  // the legacy resume flow.
  const generateForBuildClick = async (
    prompt: string,
    entityType: CustomDashAssistantEntityType
  ) =>
    runHeadlessBuildWithTrace({
      prompt,
      entityType,
      conversation: toConversationPayload(
        buildBoundedConversationContext({
          transcript: latestTranscriptRef.current,
          summary: conversationSummary,
        })
      ),
      newId: () => crypto.randomUUID(),
      nowMs: () => Date.now(),
      appendTranscript,
      updateReasoningTrace,
      updateAssistantMessageText,
      setGenerationStatus,
    });

  // Landing narration for resolve-first builds: builds the consume-once
  // `onSeeded` callback the build click stashes with its generated page —
  // "Reading the page data" while the seeded draft's widgets settle, then a
  // silent narrate turn grounded in their digests (seeded-build-narration.ts).
  // Deps go through refs because the collector completes up to 30s after the
  // click, long past this render's closures.
  const beginSeededBuildNarrationDep = (params: {
    prompt: string;
    targetPathname: string;
    filters?: TalentDiscoveryAiFilterSearchResponseV2;
    resumeTrace?: { traceId: string; trace: ReasoningTrace };
  }) =>
    beginSeededBuildNarration(params, {
      newId: () => crypto.randomUUID(),
      nowMs: () => Date.now(),
      appendTranscript,
      updateReasoningTrace,
      startAssistantTurn: (prompt, options) =>
        startAssistantTurnRef.current(prompt, options),
      getCurrentPathname: () => pathnameRef.current,
      collectorRef: seededBuildCollectorRef,
    });

  const offerWiring = useGlobalAiOfferWiring({
    transcript,
    isTurnRunning,
    isCommandInFlight,
    userTabs,
    customDashAccess,
    navigateToMatch,
    startCustomPageCreate,
    generateForBuildClick,
    beginSeededBuildNarration: beginSeededBuildNarrationDep,
    markNavConfirmResolved,
    markCreatePageConfirmResolved,
    startAssistantTurn,
    armNavResume: continuation.armNavResume,
    armCreatePageResume: continuation.armCreatePageResume,
    armBuildHere: continuation.armBuildHere,
    transcriptScrollRoot,
  });

  const applySingleChartFiltersToCurrentPage = (
    msg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    setError(null);
    const currentPath = location.pathname;
    const isCurrentPageFilterCapable =
      isGlobalAssistantFilterApplyCapablePath(currentPath) ||
      customDashAssistContextRef.current.isInCustomDashEditMode;
    if (!isCurrentPageFilterCapable) {
      setError('Filters cannot be applied on this page.');
      return;
    }
    const promptForFilters =
      msg.prompt?.trim() || lastUserPrompt?.trim() || undefined;
    if (!promptForFilters) {
      setError('Run a prompt first so we know which filters to apply.');
      return;
    }
    mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPTION, {
      choice: 'single_chart_apply_filters_current_page',
      navigate_path: currentPath,
    });
    appendTranscript({
      id: crypto.randomUUID(),
      role: 'assistant',
      kind: 'text',
      text: SAME_PAGE_FILTER_APPLY_MESSAGE,
    });
    queueRouteFilterApply({
      path: currentPath,
      promptForFilters,
      entityType: inferEntityTypeFromNavigatePath(
        currentPath,
        sessionEntityScope ?? pathEntity
      ),
    });
  };

  // --- Tool-loop runner wiring -------------------------------------------
  // The runner drives a turn through the AI-assistant tool loop. It reuses
  // the drawer's per-tool closures via these deps so the loop's client tools
  // (navigate / apply_filters / update_current_page / create_custom_page /
  // offer_report-as-chip) run through the exact same command-store + page
  // machinery the legacy pipeline used.
  const offerReportFromLoop = (
    reportTypes: string[],
    prompt: string
  ): { reportNames: string[]; messageId?: string } => {
    const uniqueReportTypes = [...new Set(reportTypes)];
    // Resolve EACH requested report type to its builder config, skipping any
    // type we can't resolve (don't crash / don't render an empty chip).
    const offers = uniqueReportTypes.flatMap((reportType) => {
      const reportConfig = reportBuilderConfig[reportType];
      if (!reportConfig) {
        return [];
      }
      const description =
        reportConfig.description ??
        getGlobalAssistantReportCatalogEntry(
          reportType as Parameters<
            typeof getGlobalAssistantReportCatalogEntry
          >[0]
        )?.description;
      return [
        {
          reportType,
          reportName: reportConfig.label ?? reportType,
          sampleLink: reportConfig.sampleLink ?? '',
          confidence: 1,
          ...(description ? { description } : {}),
        },
      ];
    });

    if (offers.length === 0) {
      return { reportNames: [] };
    }

    mixpanel.track(
      BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_REPORT_OFFERED,
      {
        global_assistant_report_type: offers
          .map((offer) => offer.reportType)
          .join(','),
        global_assistant_report_offer_count: offers.length,
      }
    );
    // Capture the params prompt NOW, at offer time, not at click time. The
    // model's offer_report call sometimes omits `prompt`; if we stored it empty
    // the click would fall back to the LIVE `lastUserPrompt`, which has since
    // moved on (or was cleared) — so the report builder opened with a completely
    // empty entity selector (no extraction ran). Freezing the offer-time prompt
    // keeps the prefill grounded in the ask that produced the offer.
    const promptForOffer = prompt?.trim() || lastUserPrompt?.trim() || '';
    const messageId = crypto.randomUUID();
    appendTranscript({
      id: messageId,
      role: 'assistant',
      kind: 'report_offers',
      offers,
      prompt: promptForOffer,
    });
    return { reportNames: offers.map((offer) => offer.reportName), messageId };
  };

  const resolveAndEmitRouteFilters = async (args: {
    requestId: string;
    path: string;
    promptForFilters: string;
    entityType: GlobalAssistantEntityType;
  }): Promise<TalentDiscoveryAiFilterSearchResponseV2> => {
    const { requestId, path, promptForFilters, entityType } = args;
    const filterKind = getGlobalAssistantRouteFilterKind(path);
    const resolved = await getRouteFilterResponse(
      promptForFilters,
      taxonomyByEntity(entityType),
      {
        filterKind,
        screenerEntityType: toCustomDashAssistantEntityType(entityType),
      }
    );
    emit({
      kind: 'applyRouteFilters',
      requestId,
      ...resolved,
      targetPath: path,
      entityType,
      prompt: promptForFilters,
    });
    // The loop's apply_filters digest builder consumes the TD v2 shape; for
    // screener resolutions the response shape differs but the drawer still
    // applies it via the command. Cast to the TD type the runner expects.
    return resolved.response as TalentDiscoveryAiFilterSearchResponseV2;
  };

  const emitResolvedRouteFilters = (args: {
    requestId: string;
    path: string;
    response:
      | TalentDiscoveryAiFilterSearchResponseV2
      | CompanyDiscoveryAiOutput;
    prompt: string;
    entityType: GlobalAssistantEntityType;
    merge?: boolean;
    /** See the `applyRouteFilters` command docblock — resolved-artifact-link applies only. */
    pairTaxonomy?: boolean;
  }): void => {
    const {
      requestId,
      path,
      response,
      prompt,
      entityType,
      merge,
      pairTaxonomy,
    } = args;
    // The `search_filters` server tool already resolved these filters into the
    // given response; emit the apply command directly with NO AI re-resolution.
    // Derive the apply kind from the path (screener vs tdV2) so company
    // discovery artifacts route to the bridge's screener applier — mirroring
    // `queueRouteFilterApply` / `resolveAndEmitRouteFilters`.
    const filterKind = getGlobalAssistantRouteFilterKind(path);
    const resolvedRouteFilters: GlobalAssistantResolvedRouteFilters =
      filterKind === 'screener'
        ? { filterKind, response: response as CompanyDiscoveryAiOutput }
        : {
            filterKind,
            response: response as TalentDiscoveryAiFilterSearchResponseV2,
          };
    emit({
      kind: 'applyRouteFilters',
      requestId,
      ...resolvedRouteFilters,
      targetPath: path,
      entityType,
      prompt,
      ...(merge ? { merge: true } : {}),
      ...(pairTaxonomy ? { pairTaxonomy: true } : {}),
    });
  };

  const { runTurn } = useGlobalAiTurnRunner({
    resolveAndEmitRouteFilters,
    emitResolvedRouteFilters,
    navigateToOptionId: async (optionId) =>
      // Enforce the SAME route-access/entitlement gate the manual chip uses
      // and report navigation honestly (no_access / unknown_page / cancelled)
      // so the loop never narrates a phantom navigation (FE-2117).
      //
      // Do NOT forward lastUserPrompt as resolvedPromptForApply: the loop's
      // `navigate` tool is pure navigation. Attaching the prompt would cause
      // getDiscoveryApplyPrompt to resolve a non-empty string for discovery
      // pages, triggering an unintended drawer-side resolve+apply of stale
      // filters from the previous turn.
      resolveNavigateToOptionId(optionId, userTabs, (match) =>
        navigateToMatch(match, { standard: true, skipFilterApply: true })
      ),
    emitUpdateCurrentPage: (requestId, prompt) => {
      const matchedEntityType = inferEntityTypeFromNavigatePath(
        location.pathname,
        sessionEntityScope ?? pathEntity
      );
      const entityType = isCustomDashAssistantEntityType(matchedEntityType)
        ? matchedEntityType
        : 'company';
      const fullConversationContext = buildBoundedConversationContext({
        transcript,
        summary: conversationSummary,
      });
      const conversationPayload = toConversationPayload(
        fullConversationContext
      );
      emit({
        kind: 'startCustomPageStream',
        requestId,
        prompt,
        entityType,
        mode: 'update',
        ...(conversationPayload ? { conversation: conversationPayload } : {}),
      });
      setGenerationStatus('Updating page…');
    },
    pendingCustomPageBuildRef: continuation.pendingCustomPageBuildRef,
    // Build-here: emit a CREATE page-gen for the CURRENT draft (no nav, no new
    // draft). Used by the confirm-navigate-resume flow, where the chip click
    // already created the empty draft + navigated to it. The bridge's create
    // handler matches on `editMode && targetPath === current path` and
    // dispatches the generated layout into the (empty) current tab, so passing
    // the current path here populates this draft in place — no `createPageDraft`
    // / `navigate`. CREATE mode (vs update) keeps the build framed as a fresh
    // dashboard for the model's reasoning + grounded narration.
    emitCreateCurrentPage: (requestId, prompt) => {
      const matchedEntityType = inferEntityTypeFromNavigatePath(
        window.location.pathname,
        sessionEntityScope ?? pathEntity
      );
      const entityType = isCustomDashAssistantEntityType(matchedEntityType)
        ? matchedEntityType
        : 'company';
      const fullConversationContext = buildBoundedConversationContext({
        transcript,
        summary: conversationSummary,
      });
      const conversationPayload = toConversationPayload(
        fullConversationContext
      );
      emit({
        kind: 'startCustomPageStream',
        requestId,
        prompt,
        entityType,
        mode: 'create',
        targetPath: window.location.pathname,
        ...(conversationPayload ? { conversation: conversationPayload } : {}),
      });
      setGenerationStatus('Designing your dashboard…');
    },
    offerReport: offerReportFromLoop,
    // Read the LIVE browser path, not the closed-over React Router `location`.
    // The turn runner captures `deps` once at turn start; when the turn itself
    // navigates (e.g. navigate → search_filters → apply_filters), a closure over
    // the turn-start `location` would still report the old page, so apply_filters
    // would target the page we LEFT and the bridge (now on the new page) would
    // never match the command. `window.location.pathname` is always current.
    currentPageLabel: () => window.location.pathname,
    inferEntityTypeForPath: (path) =>
      inferEntityTypeFromNavigatePath(path, sessionEntityScope ?? pathEntity),
    buildTurnContext: () => {
      // Mirror the legacy `suggest`/pipeline context assembly: page-level
      // taxonomy for the current route's entity, the current-page descriptor,
      // and the custom-dash edit-mode flags. Only include fields we can fill.
      const entityType = inferEntityTypeFromNavigatePath(
        location.pathname,
        sessionEntityScope ?? pathEntity
      );
      const taxonomy = taxonomyByEntity(entityType);
      const customDashContext = customDashAssistContextRef.current;
      const currentPage = buildGlobalAssistantCurrentPageContext({
        pathname: location.pathname,
        isInCustomDashEditMode: customDashContext.isInCustomDashEditMode,
        ...(lastAppliedFilterContextByPath[location.pathname]
          ? {
              lastAppliedFilterContext:
                lastAppliedFilterContextByPath[location.pathname],
            }
          : {}),
      });
      const currentPageFields: {
        pageKind?: string;
        canFilter?: boolean;
        baseFilterPrompt?: string;
      } = {
        pageKind: currentPage.kind,
        canFilter: isGlobalAssistantCurrentPageFilterable({
          pathname: location.pathname,
          isInCustomDashEditMode: customDashContext.isInCustomDashEditMode,
        }),
        ...('baseFilterPrompt' in currentPage && currentPage.baseFilterPrompt
          ? { baseFilterPrompt: currentPage.baseFilterPrompt }
          : {}),
      };
      // Surface only the standard pages this account can open: the BE strips
      // these denied ids from the navigate candidate enum and the pages grid,
      // so the model never proposes or calls an inaccessible page (FE-2117).
      const deniedNavigateOptionIds =
        getDeniedStandardNavigateOptionIds(userTabs);
      return {
        ...(taxonomy.rolesTax ? { rolesTax: taxonomy.rolesTax } : {}),
        ...(typeof taxonomy.skillsTax === 'number'
          ? { skillsTax: taxonomy.skillsTax }
          : {}),
        currentPage: currentPageFields,
        customDash: {
          inCustomDashEditMode: customDashContext.isInCustomDashEditMode,
          hasCurrentPageItems: customDashContext.hasCurrentPageItems,
        },
        ...(deniedNavigateOptionIds.length > 0
          ? { deniedNavigateOptionIds }
          : {}),
      };
    },
  });

  // While a reasoning trace is actively running it already shows the live
  // "Working for Xs" timer + a spinning current step, so the bottom-of-chat
  // status spinner underneath it is redundant — suppress it until the trace ends.
  const hasActiveReasoningTrace = transcript.some(
    (m) =>
      m.role === 'assistant' &&
      m.kind === 'reasoning_trace' &&
      m.trace.endedAt === undefined
  );

  return (
    <Box
      ref={drawerPanelRef}
      position="fixed"
      right={GLOBAL_AI_OVERLAY_RIGHT}
      bottom={GLOBAL_AI_OVERLAY_BOTTOM}
      width={GLOBAL_AI_OVERLAY_WIDTH}
      maxW="calc(100vw - 48px)"
      h={GLOBAL_AI_OVERLAY_HEIGHT}
      maxH={GLOBAL_AI_OVERLAY_HEIGHT}
      className={styles.drawer}
      zIndex={GLOBAL_AI_OVERLAY_Z_INDEX}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      pointerEvents={isDrawerOpen ? 'auto' : 'none'}
      opacity={isDrawerOpen ? 1 : 0}
      transform={
        isDrawerOpen ? 'translateY(0) scale(1)' : 'translateY(12px) scale(0.98)'
      }
      transition="opacity 0.2s ease-out, transform 0.2s ease-out"
      aria-hidden={isDrawerOpen ? undefined : true}
    >
      <Flex
        align="center"
        justify="space-between"
        h="36px"
        px={3}
        flexShrink={0}
        className={styles.headerBorder}
      >
        <IconButton
          aria-label="Clear session"
          icon={<EditIcon boxSize={4} />}
          size="xs"
          variant="ghost"
          className={styles.iconBtn}
          onClick={handleNewSession}
          isDisabled={isTurnRunning || isReportOfferLaunching}
          w={ASSISTANT_ICON_SIZE}
          h={ASSISTANT_ICON_SIZE}
          minW={ASSISTANT_ICON_SIZE}
          p={0}
          _hover={{ bg: 'blackAlpha.50' }}
          _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
        />
        <Heading
          size="sm"
          className={`${styles.text} ${styles.headerTitle}`}
          fontWeight="600"
          lineHeight="20px"
          noOfLines={1}
          flex={1}
          textAlign="left"
          px={2}
        >
          Chat
        </Heading>
        <IconButton
          aria-label="Close Chat"
          icon={<CloseIcon boxSize={3} />}
          size="sm"
          variant="ghost"
          className={styles.iconBtn}
          onClick={onCloseAndStopRecording}
          _hover={{ bg: 'blackAlpha.50' }}
        />
      </Flex>

      <Flex
        direction="column"
        flex={1}
        minH={0}
        overflow="hidden"
        px={3}
        pt={2}
      >
        <Box
          ref={handleTranscriptScrollRootRef}
          className={styles.scrollArea}
          flex={1}
          minH={0}
          w="100%"
          overflowY="auto"
          overflowX="hidden"
          display="flex"
          flexDirection="column"
          alignItems="stretch"
          data-testid="global-ai-assistant-transcript"
          pb={6}
          pr={2}
          sx={{
            '& > *': {
              flexShrink: 0,
            },
          }}
        >
          {transcript.length === 0 && !isTurnRunning ? (
            <Flex align="flex-start" gap={2}>
              <AssistantAvatar />
              <Text className={`${styles.textMuted} ${styles.bodyText}`}>
                Ask about the workforce
              </Text>
            </Flex>
          ) : null}
          <TranscriptMessages
            transcript={transcript}
            messagesById={offerWiring.messagesById}
            stampedOfferKindsByTargetId={
              offerWiring.stampedOfferKindsByTargetId
            }
            reportOfferLaunchingKey={reportOfferLaunchingKey}
            isTurnRunning={isTurnRunning}
            isCommandInFlight={isCommandInFlight}
            lastUserMessageId={lastUserMessageId}
            lastUserMessageRef={lastUserMessageRef}
            lastUserPrompt={lastUserPrompt}
            currentPathname={location.pathname}
            isInCustomDashEditMode={() =>
              customDashAssistContextRef.current.isInCustomDashEditMode
            }
            transcriptScrollRoot={transcriptScrollRoot}
            userTabs={userTabs}
            customDashAccess={customDashAccess}
            fireNavOffer={offerWiring.fireNavOffer}
            fireBuildOffer={offerWiring.fireBuildOffer}
            fireStandardPageOffer={offerWiring.fireStandardPageOffer}
            getStandardPageOfferState={offerWiring.getStandardPageOfferState}
            applySingleChartFiltersToCurrentPage={
              applySingleChartFiltersToCurrentPage
            }
            onReportOfferClick={({ messageId, offer, prompt }) => {
              // The exact call the old card's "Open report builder" button made,
              // now the ONLY report-offer action — fired by inline triggers.
              void handleReportOfferClick(offer, prompt, messageId);
            }}
          />
          {/* The reasoning trace already streams the live narration, so the
              bottom status spinner is redundant whenever a trace is active —
              suppress it (status AND the idle "Thinking…" below). It only shows
              for the rare turn that produces no trace. */}
          {generationStatus && !hasActiveReasoningTrace ? (
            <Flex align="center" gap={2} py={2} justify="center" px={1}>
              <Spinner size="sm" color="var(--ui-text-normal, #2D426A)" />
              <Text
                fontSize="12px"
                className={styles.text}
                opacity={0.9}
                textAlign="center"
                data-testid="global-ai-assistant-generation-status"
              >
                {generationStatus}
              </Text>
            </Flex>
          ) : null}
          {isTurnRunning && !generationStatus && !hasActiveReasoningTrace && (
            <Flex align="center" gap={2} py={3} justify="center">
              <Spinner size="sm" color="var(--ui-text-normal, #2D426A)" />
              <Text fontSize="12px" className={styles.text} opacity={0.8}>
                Thinking…
              </Text>
            </Flex>
          )}
          {error && (
            <Alert
              status="error"
              className={styles.alert}
              fontSize="xs"
              mb={3}
              py={2}
            >
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold" fontSize="11px">
                  Something went wrong
                </Text>
                <Text fontSize="11px">{error}</Text>
              </Box>
            </Alert>
          )}
          {/* FE-2172: reserves room so the latest question can scroll to the top
              of the viewport; shrinks toward 0 as the answer fills the space. */}
          {bottomSpacerHeight > 0 ? (
            <Box aria-hidden flexShrink={0} h={`${bottomSpacerHeight}px`} />
          ) : null}
        </Box>

        <Box flexShrink={0} pt={2} pb={3}>
          <Box
            boxSizing="border-box"
            position="relative"
            display="flex"
            alignItems="flex-end"
            gap={1}
            px={2}
            py={1}
            minH="35px"
            className={styles.inputWrap}
          >
            <Textarea
              ref={textareaRef}
              data-testid="global-ai-assistant-prompt"
              placeholder="Ask AI..."
              value={draftPrompt}
              onChange={handlePromptChange}
              lineHeight="18px"
              border="none"
              bg="transparent"
              resize="none"
              overflowY="auto"
              p={2}
              pr={1}
              rows={1}
              maxH="120px"
              flex={1}
              minH="20px"
              className={styles.input}
              style={{
                height: 'auto',
                minHeight: '20px',
              }}
              _focus={{
                outline: 'none',
                boxShadow: 'none',
              }}
              isDisabled={isTurnRunning || isReportOfferLaunching}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSubmit();
                }
                if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  textareaRef.current?.select();
                }
              }}
            />
            <Flex align="center" gap={0} flexShrink={0} pb="2px">
              <SpeechInput
                ref={speechInputRef}
                onTranscriptComplete={(transcriptText) => {
                  setDraftPrompt((prev) =>
                    prev ? `${prev} ${transcriptText}` : transcriptText
                  );
                }}
                onEnter={() => void handleSubmit()}
                onStartListening={noop}
                textAreaRef={textareaRef}
                idleIconColor="var(--ui-text-muted, rgba(45, 66, 106, 0.5))"
              />
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}
